/**
 * Phone helpers for the call-center agent forms.
 *
 * The Support-center API interpolates `Agent.phone` straight into a Twilio
 * `<Dial>` verb, so it only accepts strict E.164 (`+15145550123`). Admins
 * type numbers the way they see them on a business card — we normalize on
 * the way in and pretty-print on the way out.
 */

const E164_PATTERN = /^\+[1-9]\d{7,14}$/

/**
 * `"514 555-0123"`, `"(514) 555-0123"`, `"1 514 555 0123"`, `"+15145550123"`
 * → `"+15145550123"`. Bare 10-digit numbers are assumed North American (+1).
 * Returns `null` when the input can't be made into a valid E.164 number.
 */
export function normalizePhoneToE164(input: string): string | null {
	const raw = input.trim()
	if (!raw) return null
	const hasPlus = raw.startsWith('+')
	const digits = raw.replace(/\D/g, '')
	if (!digits) return null

	let candidate: string
	if (hasPlus) {
		candidate = `+${digits}`
	} else if (digits.length === 10) {
		candidate = `+1${digits}`
	} else if (digits.length === 11 && digits.startsWith('1')) {
		candidate = `+${digits}`
	} else {
		return null
	}

	return E164_PATTERN.test(candidate) ? candidate : null
}

/**
 * `"+15145550123"` → `"514 555-0123"`. Agents created before the API existed
 * were stored as bare national numbers (`"5142588994"`, `"15142588994"`), so
 * those are formatted too; anything else is shown as stored.
 */
export function formatPhone(phone: string): string {
	// Some legacy rows carry invisible bidi marks (U+202D/U+202C) pasted from
	// iOS Contacts — keep only digits and a leading "+" before matching.
	const cleaned = phone.replace(/[^\d+]/g, '')
	const match = /^(?:\+?1)?(\d{3})(\d{3})(\d{4})$/.exec(cleaned)
	if (!match) return phone
	return `${match[1]} ${match[2]}-${match[3]}`
}
